import { EventMeta } from '../../../interfaces/meta.interface';

type MailboxPermissionDescFields = {
    mailbox: string;
    accessRights: string;
};

const MailboxPermission: EventMeta<MailboxPermissionDescFields> = {
    title: 'Mailbox Permissions Added',
    default: {
        description: 'Mailbox Permissions Added',
        longDescription: 'Permission was added to a mailbox.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => ({
        mailbox: log.mailbox,
        accessRights: log.accessRights,
    }),
    classifications: [
        {
            severity: 'critical',
            description: (fields) => `A suspicious IP attempted to add mailbox permission`,
            longDescription: (fields) =>
                `Mailbox permissions were added from a suspicious IP address that is not in your allowed list. This activity could indicate an attempt to gain unauthorized access or control over mailbox contents.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'danger',
            description: (fields) => `An unapproved IP attempted to add mailbox permission`,
            longDescription: (fields) =>
                `Mailbox permissions were added from an IP address that is not in your allowed list. This activity may indicate an attempt to access or manage mailbox contents without authorization.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP added a mailbox permission`,
            longDescription: (fields) =>
                `Permission was added to a mailbox. The action originated from a suspicious IP. While mailbox permissions are often modified during routine administration, the use of a suspicious IP makes this event noteworthy. It is logged here for visibility. This event is informational — no immediate action is required, but monitoring for repeated mailbox changes from suspicious IPs may be useful.`,
            predicates: {
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'info',
            description: (fields) =>
                `A Microsoft system operation (synchronization server IP) added a mailbox permission`,
            longDescription: (fields) =>
                `Permission was added to a mailbox. This was recorded as a Microsoft system operation event, with the IP address tied to the server responsible for handling synchronization. This entry is informational and reflects standard background activity. No immediate action is required unless the mailbox permission change was unexpected.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        {
            severity: 'info',
            description: (fields) => `A mailbox permission was added`,
            longDescription: (fields) =>
                `Permission was added to a mailbox. This is an informational record of a mailbox configuration change. No unusual conditions were detected with this event. It does not require immediate action unless the permission change was unexpected.`,
        },
    ],
};

export default MailboxPermission;
